import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  MATERIAL_ICON_TO_FONTAWESOME,
  type FontAwesomeIconMapping,
} from './material-icon-to-fontawesome-icon-map';

function toIconPropString(prefix: string, iconName: string): string {
  return `['${prefix}', '${iconName}']`;
}

function getPlannedPrefix(mapping: FontAwesomeIconMapping): string {
  return mapping.plannedPrefix ?? mapping.previewIcon.prefix;
}

function getPlannedIconName(mapping: FontAwesomeIconMapping): string {
  return mapping.plannedIconName ?? mapping.previewIcon.iconName;
}

/**
 * Canonical map: Material icon name -> planned Font Awesome IconProp tuple.
 *
 * This is derived from `MATERIAL_ICON_TO_FONTAWESOME` using the same fallback logic
 * as the Design System inventory page:
 * - plannedPrefix/plannedIconName override
 * - otherwise fallback to previewIcon.prefix / previewIcon.iconName
 */
export const MATERIAL_ICON_TO_ICONPROP: Record<string, IconProp> = Object.fromEntries(
  Object.entries(MATERIAL_ICON_TO_FONTAWESOME).map(([materialIconName, mapping]) => {
    const prefix = getPlannedPrefix(mapping);
    const iconName = getPlannedIconName(mapping);

    // IconProp is intentionally tuple-based here to match our end-state templates.
    return [materialIconName, [prefix, iconName] as IconProp];
  }),
);

/**
 * Canonical map: Material icon name -> stringified planned IconProp tuple.
 *
 * Format intentionally matches the Design System inventory page output.
 */
export const MATERIAL_ICON_TO_ICONPROP_STRING: Record<string, string> = Object.fromEntries(
  Object.entries(MATERIAL_ICON_TO_FONTAWESOME).map(([materialIconName, mapping]) => {
    const prefix = getPlannedPrefix(mapping);
    const iconName = getPlannedIconName(mapping);
    return [materialIconName, toIconPropString(prefix, iconName)];
  }),
);

